import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { IconSetType, SvgIcon } from '@gik/ui';
import Checkbox from '@gik/ui/Checkbox';
import { UISize } from '@gik/ui/types';
import noop from '@gik/core/utils/noop';
import { PropsWithClassName } from '@gik/core/utils/ReactUtils';
import { openPremiumPageUpgradeModal } from '@gik/inkind-page/components/PremiumPageUpgradeModal/PremiumPageUpgradeModal';

export interface IGenericCardProps extends Pick<IPrivacySettingsCardProps, 'value' | 'onChange' | 'inputName'> {}

export interface IPrivacySettingsCardProps {
  icon: IconSetType;
  title: string;
  description: string;
  value: boolean;
  inputName?: string;
  onChange?: (isToggled: boolean) => void;
  note?: JSX.Element;
  checkboxSize?: UISize;
  locked?: boolean;
  id?: string;
}

export const privacySettingsCardBlockName = 'privacy-settings-card';

const PrivacySettingsCard: React.FC<PropsWithClassName<IPrivacySettingsCardProps>> = ({
  icon,
  title,
  description,
  value,
  note,
  checkboxSize = 'lg',
  className,
  id,
  inputName,
  locked,
  onChange = noop,
}): JSX.Element => {
  const bem = bemBlock(privacySettingsCardBlockName);

  const handleToggle = async (toggled: boolean) => {
    if (locked) {
      openPremiumPageUpgradeModal({
        initiatedOn: 'inkindPageSettings',
        variant: 'hideSuggestions',
        onPurchase: () => onChange(toggled),
      });
      return;
    }
    onChange(toggled);
  };

  return (
    <section className={bem(null, null, className)} id={id}>
      <div className={bem('card')}>
        <SvgIcon className={bem('icon')} {...icon} />

        <div className={bem('text')}>
          <label htmlFor={inputName} className={bem('title')}>
            {title}
          </label>
          <p className={bem('description')}>{description}</p>
        </div>

        {locked && <SvgIcon className={bem('padlock')} name="LockClosed" />}
        <Checkbox
          className={bem('checkbox')}
          size={checkboxSize}
          id={inputName}
          name={inputName}
          checked={value}
          onValueChange={toggled => handleToggle(toggled)}
        />
      </div>
      {note && <p className={bem('note')}>{note}</p>}
    </section>
  );
};

export default PrivacySettingsCard;
